"use client";

import { TIERS, getPriceForCurrency, TierId } from "@mw/shared/pricing";
import { useTranslations } from "next-intl";
import { cva } from "class-variance-authority";
import { useCurrency } from "@/app/_providers/currency";
import { formatNumber } from "./format-utils";
import { PricingTooltip } from "./PricingTooltip";
import { cn } from "@ui/lib/utils";
import type { Section } from "./pricing-sections";

// Feature availability map - defines which tiers have access to which sections
export type SectionAvailability = Partial<Record<Section, Record<TierId, boolean>>>;

// Column count type for grid CVAs
export type ColumnCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// Define all variants for sections that have multiple configurations
export interface SectionVariant {
  label?: string;
  modelOverride?: "highest_quality" | "flash_turbo";
  sttInterfaceOverride?: "api" | "ui";
  sttVersionOverride?: "v1" | "realtime_v2";
  dubbingProductOverride?: "automatic_dubbing" | "dubbing_studio";
  dubbingWatermarkOverride?: "watermark" | "no_watermark";
}

export const getSectionVariants = (
  section: Section,
  t: ReturnType<typeof useTranslations<"PricingClientKeys.Pricing">>,
): SectionVariant[] => {
  switch (section) {
    case "text_to_speech":
      // Render both models as separate rows
      return [
        { label: t("variant_labels.multilingual_v2_v3"), modelOverride: "highest_quality" },
        { label: t("variant_labels.flash"), modelOverride: "flash_turbo" },
      ];
    case "speech_to_text": {
      // Render all combinations of version and interface
      const variants: SectionVariant[] = [
        { label: t("variant_labels.scribe_v1_v2_api"), sttVersionOverride: "v1", sttInterfaceOverride: "api" },
        { label: t("variant_labels.scribe_v1_v2_ui"), sttVersionOverride: "v1", sttInterfaceOverride: "ui" },
        {
          label: t("variant_labels.scribe_v2_realtime_api"),
          sttVersionOverride: "realtime_v2",
          sttInterfaceOverride: "api",
        },
      ];
      return variants;
    }
    case "dubbing":
      // Legacy combined dubbing - render all combinations of product and watermark
      return [
        {
          label: t("variant_labels.automatic_dubbing_no_watermark"),
          dubbingProductOverride: "automatic_dubbing",
          dubbingWatermarkOverride: "no_watermark",
        },
        {
          label: t("variant_labels.automatic_dubbing_with_watermark"),
          dubbingProductOverride: "automatic_dubbing",
          dubbingWatermarkOverride: "watermark",
        },
        {
          label: t("variant_labels.dubbing_studio_no_watermark"),
          dubbingProductOverride: "dubbing_studio",
          dubbingWatermarkOverride: "no_watermark",
        },
        {
          label: t("variant_labels.dubbing_studio_with_watermark"),
          dubbingProductOverride: "dubbing_studio",
          dubbingWatermarkOverride: "watermark",
        },
      ];
    case "automatic_dubbing":
      // Render automatic dubbing variants
      return [
        {
          label: t("variant_labels.with_watermark"),
          dubbingProductOverride: "automatic_dubbing",
          dubbingWatermarkOverride: "watermark",
        },
        {
          label: t("variant_labels.no_watermark"),
          dubbingProductOverride: "automatic_dubbing",
          dubbingWatermarkOverride: "no_watermark",
        },
      ];
    case "dubbing_studio":
      // Render dubbing studio variants
      return [
        {
          label: t("variant_labels.with_watermark"),
          dubbingProductOverride: "dubbing_studio",
          dubbingWatermarkOverride: "watermark",
        },
        {
          label: t("variant_labels.no_watermark"),
          dubbingProductOverride: "dubbing_studio",
          dubbingWatermarkOverride: "no_watermark",
        },
      ];
    default:
      // Single row for all other sections
      return [{}];
  }
};

// Default availability - everything is available except dubbing_studio which starts from creator
export const DEFAULT_SECTION_AVAILABILITY: SectionAvailability = {
  dubbing_studio: {
    free: false,
    starter: true,
    creator: true,
    pro: true,
    scale: true,
    scale_2024_08_10: true,
    enterprise: true,
    payg: false,
  },
};

// CVA for responsive grid columns based on tier count (with feature label column)
export const pricingTableGrid = cva("tw-grid tw-grid-cols-2", {
  variants: {
    columnCount: {
      1: "md:tw-grid-cols-[minmax(15rem,1fr)_minmax(10rem,1fr)_3rem]",
      2: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(2,minmax(10rem,1fr))_3rem]",
      3: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(3,minmax(10rem,1fr))_3rem]",
      4: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(4,minmax(10rem,1fr))_3rem]",
      5: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(5,minmax(10rem,1fr))_3rem]",
      6: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(6,minmax(10rem,1fr))_3rem]",
      7: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(7,minmax(10rem,1fr))_3rem]",
      8: "md:tw-grid-cols-[minmax(15rem,1fr)_repeat(8,minmax(10rem,1fr))_3rem]",
    },
  },
  defaultVariants: {
    columnCount: 4,
  },
});

// CVA for content grid that includes feature label column (sidebar variant)
export const contentGridWithLabels = cva("tw-grid tw-grid-cols-2", {
  variants: {
    columnCount: {
      1: "md:tw-grid-cols-[minmax(12rem,1fr)_minmax(9rem,1fr)]",
      2: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(2,minmax(9rem,1fr))]",
      3: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(3,minmax(9rem,1fr))]",
      4: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(4,minmax(9rem,1fr))]",
      5: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(5,minmax(9rem,1fr))]",
      6: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(6,minmax(9rem,1fr))]",
      7: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(7,minmax(9rem,1fr))]",
      8: "md:tw-grid-cols-[minmax(12rem,1fr)_repeat(8,minmax(9rem,1fr))]",
    },
  },
  defaultVariants: {
    columnCount: 4,
  },
});

// Shared gradient style for section headers and subheaders
export const SECTION_HEADER_GRADIENT =
  "linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 2%, #FFF 92.71%, rgba(255, 255, 255, 0.00) 100%)";

// Hook to get productions services data
export const useProductionsServices = () => {
  const tProductions = useTranslations("PricingClientKeys.Pricing.productions");
  const { currency, currencySymbol } = useCurrency();

  // Get the free tier directly - productions pricing is always based on the free tier
  const freeTier = TIERS.free;

  if (!freeTier?.products.productions) {
    return { services: [], enterpriseFeatures: [] };
  }

  // Get enterprise features for tooltip
  // @ts-expect-error key is valid
  const enterpriseFeatures: [string, string][] = Object.entries(tProductions.raw("card.features"));

  const productionsPricing = freeTier.products.productions!.pricing;
  const services = [
    {
      key: "transcripts_per_minute",
      title: tProductions("services.transcripts_per_minute.title"),
      tooltip: tProductions.raw("services.transcripts_per_minute.tooltip"),
      selfServeValue: tProductions("services.transcripts_per_minute.cost", {
        cost: formatNumber(getPriceForCurrency(productionsPricing.transcriptsPerMinute ?? 0, currency))!.toString(),
        symbol: currencySymbol,
      }),
      enterpriseValue: tProductions("plans.enterprise.price_value"),
    },
    {
      key: "captions_per_minute",
      title: tProductions("services.captions_per_minute.title"),
      tooltip: tProductions.raw("services.captions_per_minute.tooltip"),
      selfServeValue: tProductions("services.captions_per_minute.cost", {
        cost: formatNumber(getPriceForCurrency(productionsPricing.captionsPerMinute ?? 0, currency))!.toString(),
        symbol: currencySymbol,
      }),
      enterpriseValue: tProductions("plans.enterprise.price_value"),
    },
    {
      key: "subtitles_per_minute",
      title: tProductions("services.subtitles_per_minute.title"),
      tooltip: tProductions.raw("services.subtitles_per_minute.tooltip"),
      selfServeValue: tProductions("services.subtitles_per_minute.cost", {
        cost: formatNumber(getPriceForCurrency(productionsPricing.subtitlesPerMinute ?? 0, currency))!.toString(),
        symbol: currencySymbol,
      }),
      enterpriseValue: tProductions("plans.enterprise.price_value"),
    },
    {
      key: "dubs_per_minute",
      title: tProductions("services.dubs_per_minute.title"),
      tooltip: tProductions.raw("services.dubs_per_minute.tooltip"),
      selfServeValue: tProductions("services.dubs_per_minute.cost", {
        cost: formatNumber(getPriceForCurrency(productionsPricing.dubsPerMinute ?? 0, currency))!.toString(),
        symbol: currencySymbol,
      }),
      enterpriseValue: tProductions("plans.enterprise.price_value"),
    },
    {
      key: "audiobooks_per_hour",
      title: tProductions("services.audiobooks_per_hour.title"),
      tooltip: tProductions.raw("services.audiobooks_per_hour.tooltip"),
      selfServeValue: tProductions("services.audiobooks_per_hour.cost", {
        cost: formatNumber(getPriceForCurrency(productionsPricing.audiobooksPerHour ?? 0, currency))!.toString(),
        symbol: currencySymbol,
      }),
      enterpriseValue: tProductions("plans.enterprise.price_value"),
    },
  ];

  return {
    services,
    enterpriseFeatures,
    tProductions,
  };
};

// Shared Productions Section Content (just the rows, no wrapper)
export const ProductionsSectionContent = ({
  gridClassName,
  showFeatureColumn = true,
}: {
  gridClassName?: string;
  showFeatureColumn?: boolean;
}) => {
  const t = useTranslations("PricingClientKeys.Pricing");
  const { services, enterpriseFeatures, tProductions } = useProductionsServices();

  if (services.length === 0 || !tProductions) {
    return null;
  }

  const defaultGrid = "tw-grid md:tw-grid-cols-[minmax(12rem,1fr)_repeat(2,minmax(8rem,1fr))]";
  const grid = gridClassName || defaultGrid;

  return (
    <>
      {/* Header Row */}
      <div className={cn("tw-border-b tw-pl-10 tw-border-black/5", grid)} style={{ background: "#F9F8F6" }}>
        {showFeatureColumn && (
          <div className='tw-p-4 tw-pl-0 tw-text-left max-md:tw-hidden'>
            <span className='tw-font-medium tw-opacity-60 tw-type-sm'>{t("feature_column_header")}</span>
          </div>
        )}
        <div className='tw-p-4 tw-text-right'>
          <span className='tw-font-medium tw-opacity-60 tw-type-sm'>{tProductions("plans.free.title")}</span>
        </div>
        <div className='tw-p-4 tw-text-right tw-flex tw-items-center tw-justify-end tw-gap-1'>
          <span className='tw-font-medium tw-opacity-60 tw-type-sm'>{tProductions("plans.enterprise.title")}</span>
          <PricingTooltip
            tooltip={`<strong>${tProductions("card.title")}</strong><br/>${tProductions("card.description")}<br/><ul>${enterpriseFeatures.map(([_, value]) => `<li>${value}</li>`).join("")}</ul>`}
          />
        </div>
      </div>

      {/* Data Rows */}
      {services.map(service => (
        <div key={service.key} className={cn("tw-border-b tw-pl-10 tw-border-black/5", grid)}>
          {showFeatureColumn && (
            <div className='tw-p-4 tw-pl-0 tw-flex tw-items-center tw-gap-1 max-md:tw-hidden'>
              <span className='tw-text-gray-700 tw-type-sm'>{service.title}</span>
              <PricingTooltip tooltip={service.tooltip} />
            </div>
          )}
          <div className='tw-p-4 tw-text-right tw-flex tw-items-center tw-justify-end'>
            <span className='tw-type-sm'>{service.selfServeValue}</span>
          </div>
          <div className='tw-p-4 tw-text-right tw-flex tw-items-center tw-justify-end'>
            <span className='tw-type-sm'>{service.enterpriseValue}</span>
          </div>
        </div>
      ))}
    </>
  );
};
